import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Co2, LocalFireDepartment } from '@mui/icons-material';
import { RecipeV2Detail } from '../types';
import CarbonRatingChip from './CarbonRatingChip';

interface CarbonTabProps {
  recipe: RecipeV2Detail;
}

const CarbonTab = ({ recipe }: CarbonTabProps) => {
  const { t } = useTranslation('recipeV2');

  const wasteFactor = 0.15; // 15% waste assumption
  const carbonPerSale = recipe.carbonPerPortion;
  const carbonPerWaste = recipe.carbonTotal * wasteFactor;

  return (
    <Stack spacing={3}>
      {/* Summary */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>
          <Co2 sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('carbon.title')}
        </Typography>
        <Stack direction="row"
          spacing={4}>
          <Box>
            <Typography variant="textXS"
              color="text.secondary">{t('carbon.totalCarbon')}</Typography>
            <Typography variant="text2XL"
              fontWeight={700}>{recipe.carbonTotal.toFixed(2)} kg CO₂e</Typography>
          </Box>
          <Box>
            <Typography variant="textXS"
              color="text.secondary">{t('carbon.carbonPerPortion')}</Typography>
            <Typography variant="text2XL"
              fontWeight={700}>{carbonPerSale.toFixed(2)} kg CO₂e</Typography>
          </Box>
          <Box>
            <Typography variant="textXS"
              color="text.secondary">{t('carbon.rating')}</Typography>
            {recipe.carbonScore && (
              <CarbonRatingChip rating={recipe.carbonScore}
                size="medium"
                sx={{ fontSize: 18, height: 32 }} />
            )}
          </Box>
          <Box>
            <Typography variant="textXS"
              color="text.secondary">{t('carbon.productionMethod')}</Typography>
            <Stack direction="row"
              alignItems="center"
              spacing={1}>
              <LocalFireDepartment sx={{ color: 'warning.main' }} />
              <Typography variant="textMD"
                fontWeight={600}>
                {recipe.productionMethod?.name ?? 'N/A'} (×{recipe.productionMethod?.carbonMultiplier ?? 1})
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Sale vs Waste */}
      <Stack direction="row"
        spacing={2}>
        <Paper variant="outlined"
          sx={{ p: 2, flex: 1 }}>
          <Typography variant="textSM"
            color="text.secondary">{t('carbon.saleCarbonLabel')}</Typography>
          <Typography variant="displayXS"
            fontWeight={700}
            color="success.main">
            {carbonPerSale.toFixed(3)} kg CO₂e
          </Typography>
          <Typography variant="textXS">Per portion sold to customer</Typography>
        </Paper>
        <Paper variant="outlined"
          sx={{ p: 2, flex: 1 }}>
          <Typography variant="textSM"
            color="text.secondary">{t('carbon.wasteCarbonLabel')}</Typography>
          <Typography variant="displayXS"
            fontWeight={700}
            color="error.main">
            {carbonPerWaste.toFixed(3)} kg CO₂e
          </Typography>
          <Typography variant="textXS">Per recipe batch wasted (assumes {(wasteFactor * 100).toFixed(0)}% waste)</Typography>
        </Paper>
      </Stack>

      {/* Ingredient breakdown */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('carbon.calculationBreakdown')}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ingredient')}</TableCell>
                <TableCell align="right">{t('carbon.factor')}</TableCell>
                <TableCell align="right">{t('quantity')}</TableCell>
                <TableCell align="right">{t('carbon.carbonContribution')}</TableCell>
                <TableCell>{t('source')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipe.components.map((c) => {
                const factor = c.carbonFactor?.kgCo2ePerUnit ?? 0;
                const contribution = factor * c.quantity;
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.componentDescription}</TableCell>
                    <TableCell align="right">{factor.toFixed(3)}</TableCell>
                    <TableCell align="right">{c.quantity.toFixed(3)} {c.uom}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={contribution > 1 ? 700 : 400}>
                        {contribution.toFixed(3)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.carbonFactor?.source ?? 'No data'}
                        size="small"
                        variant="outlined"
                        color={c.carbonFactor ? 'default' : 'warning'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {recipe.productionMethod && (
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={3}>
                    <Typography fontWeight={600}>
                      {t('carbon.multiplier')}: {recipe.productionMethod.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>×{recipe.productionMethod.carbonMultiplier}</Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={700}>{t('carbon.totalCarbon')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={700}>{recipe.carbonTotal.toFixed(3)} kg CO₂e</Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};

export default CarbonTab;
